import { BufferConfig, IntercityCheckoutApi } from "services/tourService";

interface VenueInput {
  venueId: number | null;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
}

interface Params {
  venues: VenueInput[];
  arrivalDate: string;
  arrivalTime: string;
  departureDate: string;
  departureTime: string;
  buffers: BufferConfig[];
  intercityCheckouts: IntercityCheckoutApi[];
  tourType: number;
}

export const normalizeTime = (time: string) => {
  if (time.toLowerCase().includes("am") || time.toLowerCase().includes("pm")) {
    return new Date(`1970-01-01 ${time}`).toTimeString().slice(0, 5);
  }
  return time;
};

const addMinutes = (date: Date, minutes: number) => {
  return new Date(date.getTime() + minutes * 60000);
};

const subtractMinutes = (date: Date, minutes: number) => {
  return new Date(date.getTime() - minutes * 60000);
};

const formatTime = (date: Date) => {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

export const getTravelTimeMinutes = (travelTime?: string | null) => {
  if (!travelTime) return 0;

  const normalized = normalizeTime(travelTime);
  const [h, m] = normalized.split(":").map(Number);

  return h * 60 + m;
};

export const getTotalVenueBuffer = (
  buffers: BufferConfig[],
  venueId: number,
  portId?: number,
  includeTransport = true,
) => {
  const venueBuffers = buffers.filter((b) => b.venueId === venueId);

  const accommodation = venueBuffers.filter((b) => b.type === "1");

  const transport = includeTransport
    ? venueBuffers.filter((b) => b.type === "2")
    : [];

  const parseTimeToMinutes = (time: string) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const sum = (arr: BufferConfig[], key: "checkInBuffer" | "checkOutBuffer") =>
    arr.reduce((total, b) => total + parseTimeToMinutes(b[key] || "00:00"), 0);

  return {
    checkIn:
      sum(accommodation, "checkInBuffer") + sum(transport, "checkInBuffer"),

    checkOut:
      sum(accommodation, "checkOutBuffer") + sum(transport, "checkOutBuffer"),
  };
};

export const getIntercityCheckout = (
  intercityCheckouts: IntercityCheckoutApi[],
  fromVenueId: number,
  toVenueId: number,
  date: Date,
  tourType: number,
) => {
  return intercityCheckouts.find((i) => {
    if (i.fromVenueId !== fromVenueId) return false;
    if (i.toVenueId !== toVenueId) return false;
    if (i.tourType !== tourType) return false;

    // Miqaat → no date check
    if (tourType === 2) return true;

    if (!i.startDate || !i.endDate) return false;

    const start = new Date(i.startDate.split("T")[0]);
    const end = new Date(i.endDate.split("T")[0]);

    return date >= start && date <= end;
  });
};

export const parseTimeToMinutes = (time: string) => {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

export const canAccommodateAnotherVenue = ({
  venues,
  arrivalDate,
  arrivalTime,
  departureDate,
  departureTime,
  buffers,
  intercityCheckouts,
  tourType,
}: Params) => {
  const testVenues = [
    ...venues,
    {
      venueId: venues[venues.length - 1]?.venueId ?? null,
      fromDate: "",
      fromTime: "",
      toDate: "",
      toTime: "",
    },
  ];

  const result = calculateVenueSchedule({
    venues: testVenues,
    arrivalDate,
    arrivalTime,
    departureDate,
    departureTime,
    buffers,
    intercityCheckouts,
    tourType,
  });

  if (!result) {
    return false;
  }

  const lastVenue = result[result.length - 1];

  const departureDateTime = new Date(
    `${departureDate}T${normalizeTime(departureTime)}`,
  );

  const checkoutDateTime = new Date(
    `${lastVenue.toDate}T${normalizeTime(lastVenue.toTime)}`,
  );

  return checkoutDateTime <= departureDateTime;
};

export const calculateVenueSchedule = ({
  venues,
  arrivalDate,
  arrivalTime,
  departureDate,
  departureTime,
  buffers,
  intercityCheckouts,
  tourType,
}: Params): VenueInput[] | null => {
  if (
    !arrivalDate ||
    !arrivalTime ||
    !departureDate ||
    !departureTime ||
    venues.length < 2 ||
    buffers.length === 0
  ) {
    return null;
  }

  if (venues.some((v) => v.venueId == null)) return null;

  const arrival = new Date(`${arrivalDate}T${normalizeTime(arrivalTime)}`);

  const departure = new Date(
    `${departureDate}T${normalizeTime(departureTime)}`,
  );

  const totalDays = Math.max(
    1,
    Math.ceil(
      (departure.getTime() - arrival.getTime()) / (1000 * 60 * 60 * 24),
    ),
  );

  const venuesCount = venues.length;

  const baseDays = Math.floor(totalDays / venuesCount);
  let extraDays = totalDays % venuesCount;

  let runningDate = new Date(arrival);

  const updatedVenues: VenueInput[] = [];

for (let index = 0; index < venues.length; index++) {
  const venue = venues[index];
    const venueId = venue.venueId as number;

    const isLast = index === venuesCount - 1;

    let daysForVenue = baseDays;
    if (extraDays > 0) {
      daysForVenue++;
      extraDays--;
    }

    const nextDate = new Date(runningDate);
    nextDate.setDate(nextDate.getDate() + daysForVenue);

    /* check-in buffer */

    let includeTransport = true;

    if (index > 0) {
      const prevVenue = venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity = getIntercityCheckout(
          intercityCheckouts,
          prevVenue.venueId,
          venueId,
          runningDate,
          tourType,
        );

        if (intercity?.travelTime) {
          includeTransport = false;
        }
      }
    }

    const checkInBuffer = getTotalVenueBuffer(
      buffers,
      venueId,
      undefined,
      includeTransport,
    );

    const checkIn = addMinutes(runningDate, checkInBuffer.checkIn);

    /* check-out buffer */

    let checkOut: Date;
    

    if (isLast) {
      const lastBuffer = getTotalVenueBuffer(buffers, venueId, undefined, true);

      checkOut = subtractMinutes(departure, lastBuffer.checkOut);
    } else {
      const nextVenue = venues[index + 1];

      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            nextDate,
            tourType,
          )
        : null;

      if (intercity) {
        const normalized = normalizeTime(intercity.checkoutTime);
        const [hh, mm] = normalized.split(":").map(Number);

        const baseCheckout = new Date(nextDate);
        baseCheckout.setHours(hh, mm, 0, 0);

        const buffer = getTotalVenueBuffer(buffers, venueId, undefined, false);

        checkOut = subtractMinutes(baseCheckout, buffer.checkOut);
      } else {
        const buffer = getTotalVenueBuffer(buffers, venueId, undefined, false);

        checkOut = subtractMinutes(nextDate, buffer.checkOut);
      }
    }

    if (checkIn >= checkOut) {
      return null;
    }

    /* next start */

    if (!isLast) {
      let nextStart = new Date(checkOut);

      const nextVenue = venues[index + 1];

      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            nextDate,
            tourType,
          )
        : null;

      if (intercity?.travelTime) {
        const travelMinutes = getTravelTimeMinutes(intercity.travelTime);
        nextStart = addMinutes(nextStart, travelMinutes);
      }

      runningDate = nextStart;
    }

        updatedVenues.push({
      ...venue,
      fromDate: checkIn.toISOString().split("T")[0],
      fromTime: formatTime(checkIn),
      toDate: isLast ? departureDate : nextDate.toISOString().split("T")[0],
      toTime: formatTime(checkOut),
    });
  }
  return updatedVenues;
};
